import { supabase } from '../supabaseClient';

// Set to false before production launch
export const REVIEW_TEST_MODE = true;

export async function fetchBrokerReviews(brokerId, limit = 50) {
  const { data, error } = await supabase
    .from('broker_reviews')
    .select(`
      id, rating, review_text, broker_reply, broker_reply_at, created_at,
      reviewer:reviewer_id (id, full_name, avatar_url)
    `)
    .eq('broker_id', brokerId)
    .eq('is_hidden', false)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Failed to fetch reviews:', error);
    return [];
  }
  return data || [];
}

export async function fetchBrokerRatingSummary(brokerId) {
  const { data, error } = await supabase
    .from('broker_reviews')
    .select('rating')
    .eq('broker_id', brokerId)
    .eq('is_hidden', false);

  if (error || !data) {
    return { average: null, count: 0, breakdown: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } };
  }

  const count = data.length;
  if (count === 0) {
    return { average: null, count: 0, breakdown: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } };
  }

  const sum = data.reduce((acc, r) => acc + r.rating, 0);
  const average = (sum / count).toFixed(1);

  const breakdown = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  data.forEach((r) => { breakdown[r.rating]++; });

  return { average: parseFloat(average), count, breakdown };
}

export async function canUserReview(brokerId, userId) {
  if (!userId) return { canReview: false, reason: 'not_logged_in' };
  if (REVIEW_TEST_MODE) return { canReview: true, reason: 'test_mode' };
  if (brokerId === userId) return { canReview: false, reason: 'cannot_review_self' };

  const { data: existing } = await supabase
    .from('broker_reviews')
    .select('id')
    .eq('broker_id', brokerId)
    .eq('reviewer_id', userId)
    .maybeSingle();

  if (existing) return { canReview: false, reason: 'already_reviewed', existingReviewId: existing.id };

  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
  const { data: conversation } = await supabase
    .from('conversations')
    .select('id')
    .or(
      `and(participant_1.eq.${userId},participant_2.eq.${brokerId}),` +
      `and(participant_1.eq.${brokerId},participant_2.eq.${userId})`
    )
    .lt('created_at', thirtyDaysAgo)
    .limit(1)
    .maybeSingle();

  if (!conversation) return { canReview: false, reason: 'must_contact_first' };

  return { canReview: true, reason: 'eligible' };
}

export async function submitBrokerReview({ brokerId, reviewerId, rating, reviewText }) {
  const { data, error } = await supabase
    .from('broker_reviews')
    .insert({
      broker_id: brokerId,
      reviewer_id: reviewerId,
      rating,
      review_text: reviewText,
    })
    .select()
    .single();

  if (error) return { success: false, error: error.message };
  return { success: true, review: data };
}

export async function countBrokerListings(brokerEmail) {
  if (!brokerEmail) return 0;
  const { count, error } = await supabase
    .from('listings')
    .select('*', { count: 'exact', head: true })
    .eq('contact_email', brokerEmail);

  if (error) return 0;
  return count || 0;
}

export async function fetchBrokerListings(brokerEmail) {
  if (!brokerEmail) return [];
  const { data, error } = await supabase
    .from('listings')
    .select('*')
    .eq('contact_email', brokerEmail)
    .order('created_at', { ascending: false });

  if (error) return [];
  return data || [];
}
